/**
 * Loose kicks (owner: ball team; ARCHITECTURE §5 "Classic format"). A kick (`ball.kick`) that hit the ground is
 * LOOSE: it bounces and rolls (flight.ts) and is picked up by the first player whose free glove comes within
 * KICK.recoverRadius of it — or whose head touches it — and who may recover it (`mayRecoverLoose`). The receiver
 * wins a same-tick tie. A pick-up makes the ball HELD by that player (CATCH pose):
 *  - receiver → KICK_CAUGHT{fromGround: true}: a live return (the rules treat it like a caught kick);
 *  - kicker   → KICK_RECOVERED{downed}: the rules end the play on this tick (`downed` = an untouched punt).
 * The rules time a ball nobody picks up out (KICK.looseDeadFrames) and judge touchbacks.
 */
import { BALL_RADIUS, HITSTOP, KICK, yardsToGoal } from '@hf/shared';
import { addHitStop } from '../contact/fx';
import { BallState, PlayCall, PlayerState } from '../enums';
import { setState } from '../player/fsm';
import { effectiveHeadRadius } from '../specials/query';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';
import { gloveFree, mayRecoverLoose } from './reach';

const KINDS = ['kickoff', 'onside', 'punt'] as const;
/** KickEvent.kind index of the current kick call. */
export const kickKindIndex = (call: number): number =>
  call === PlayCall.PUNT ? 2 : call === PlayCall.ONSIDE ? 1 : 0;

/** `p` touches the loose ball with a free glove (within KICK.recoverRadius) or his head. */
const touches = (s: SimState, p: PlayerSim): boolean => {
  if (!gloveFree(p) || !mayRecoverLoose(s, p)) return false;
  const b = s.ball;
  const gx = b.x - (p.x + p.facing * p.gloveX);
  const gy = b.y - (p.y + p.gloveY);
  if (gx * gx + gy * gy <= KICK.recoverRadius * KICK.recoverRadius) return true;
  const hx = b.x - p.x;
  const hy = b.y - p.y;
  const hr = effectiveHeadRadius(p) + BALL_RADIUS;
  return hx * hx + hy * hy <= hr * hr;
};

const pickUp = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const b = s.ball;
  const r = s.rules;
  const receiver = p.index !== r.offense;
  const touchedByReceiver = (b.touchedMask & (1 << (1 - r.offense))) !== 0;
  b.state = BallState.HELD;
  b.holder = p.index;
  b.kick = false;
  b.touchedMask |= 1 << p.index;
  b.lastTouchedBy = p.index;
  b.vx = p.vx;
  b.vy = p.vy;
  b.angVel = 0;
  setState(p, PlayerState.CATCH);
  if (receiver) {
    if (ev) {
      // his own end zone is the one the kicker attacks
      const inEndZone = yardsToGoal(p.x, r.attackDir) <= 0;
      const kind = KINDS[kickKindIndex(r.playCall)]!;
      ev.push({
        type: 'KICK_CAUGHT',
        tick: s.tick,
        actor: p.index,
        x: b.x,
        y: b.y,
        kind,
        fromGround: true,
        inEndZone,
      });
    }
    addHitStop(s, ev, p.index, HITSTOP.catch, 'catch', b.x, b.y);
  } else if (ev) {
    const downed = r.playCall === PlayCall.PUNT && !touchedByReceiver;
    const kind = KINDS[kickKindIndex(r.playCall)]!;
    ev.push({ type: 'KICK_RECOVERED', tick: s.tick, actor: p.index, x: b.x, y: b.y, kind, downed });
  }
};

/** A LOOSE kick in a live play this tick: count its time on the ground, then the first legal pick-up. */
export const recoverLoose = (s: SimState, ev: SimEventSink): void => {
  const b = s.ball;
  b.looseFrames++;
  const recv = s.players[(1 - s.rules.offense) as 0 | 1];
  const kicker = s.players[s.rules.offense as 0 | 1];
  if (touches(s, recv)) pickUp(s, ev, recv);
  else if (touches(s, kicker)) pickUp(s, ev, kicker);
};
